import React, { FC, useCallback, useMemo } from 'react'
import { LineItem } from '@commerce/types/cart'
import { CancelReason } from './ConfirmCancelSubscription/ConfirmCancelSubscriptionView'

export interface State {
  displaySidebar: boolean
  displayDropdown: boolean
  displayModal: boolean
  onCloseModalCallback?: (result?: any) => void
  modalHideCloseButton?: boolean
  modalMobileFullscreen: boolean
  upsellInfoOnly?: boolean,
  upsellParametersStep?: boolean
  upsellShippingStep?: boolean
  upsellProductUpsell?: boolean
  upsellParentKitId?: any
  upsellingItems?: {
    parametersHeading?: string,
    parametersSubheading?: string,
    parameters?: {
      _id: string
      title: string
      description: string
      image: any
      productSlug?: string
      variantSlug?: string
      originalPrice: string
      discountedPrice: string
    }[],
    shippingMethodsHeading?: string
    shippingMethods?: {
      _id: string
      title: string
      type: string
      heading: string
      price: string
      description: string
      colorClass: string
      textColorClass: string
      features: {
        icon: string
        description: string
      }[]
    }[]
  }
  upsellProduct?: any
  upsellDiscounts?: any
  upsellLineItem?: LineItem
  sidebarView: string
  modalView: string
  userAvatar: string
  prodInfo: string
  shareableKit: any
  shareableClient: any
  prodInfo2: string
  howToVideo: string
  subscriptionBenefits?: {
    title: string,
    subtitle: string,
    benefits: {
      title: string
      description: string
      icon: string
    }[],
    faqs: {
      title: string
      description: string
    }[]
  },
  subscriptionCancelReasons?: CancelReason[]
}

const initialState = {
  displaySidebar: false,
  displayDropdown: false,
  displayModal: false,
  modalHideCloseButton: false,
  modalMobileFullscreen: false,
  upsellInfoOnly: false,
  upsellParametersStep: true,
  upsellParameters: [],
  upsellShippingStep: true,
  upsellProductUpsell: false,
  upsellParentKitId: undefined,
  upsellShippingMethods: [],
  upsellProduct: undefined,
  upsellDiscounts: undefined,
  upsellLineItem: undefined,
  modalView: 'LOGIN_VIEW',
  sidebarView: 'PROFILE_VIEW',
  userAvatar: '',
  prodInfo: '',
  shareableKit: undefined,
  shareableClient: undefined,
  prodInfo2: '',
  howToVideo: '',
  subscriptionBenefits: undefined,
  subscriptionCancelReason: undefined
}

type Action =
  | {
  type: 'OPEN_SIDEBAR'
}
  | {
  type: 'CLOSE_SIDEBAR'
}
  | {
  type: 'OPEN_DROPDOWN'
}
  | {
  type: 'CLOSE_DROPDOWN'
}
  | {
  type: 'OPEN_MODAL'
  onCloseCallback?: (result?: any) => void
  modalHideCloseButton?: boolean
  modalMobileFullscreen: boolean
}
  | {
  type: 'CLOSE_MODAL'
}
  | {
  type: 'SET_MODAL_VIEW'
  view: MODAL_VIEWS
}
  | {
  type: 'SET_SIDEBAR_VIEW'
  view: SIDEBAR_VIEWS
}
  | {
  type: 'SET_USER_AVATAR'
  value: string
}
  | {
  type: 'SET_PROD_INFO'
  value: string
}
  | {
  type: 'SET_PROD_INFO2'
  value: string
}
| {
  type: 'SET_SHAREABLE_KIT'
  value: any
}
| {
  type: 'SET_SHAREABLE_CLIENT'
  value: any
}
  | {
  type: 'SET_HOW_TO'
  value: string
}
  | {
  type: 'SET_SUBSCRIPTION_BENEFITS'
  value: {
    title: string
    subtitle: string
    benefits: {
      title: string
      description: string
      icon: string
    }[]
    faqs: {
      title: string
      description: string
    }[]
  }
}
  | {
  type: 'SET_SUBSCRIPTION_CANCEL_REASONS'
  value: CancelReason[]
}
  | {
  type: 'SET_UPSELL_INFO'
  value: {
    infoOnly?: boolean,
    parametersStep?: boolean
    shippingStep?: boolean
    subscriptionStep?: boolean
    subscriptionConfig?: any
    productUpsell?: boolean
    parentKitId?: any
    upsellingItems?: {
      parametersHeading?: string,
      parametersSubheading?: string,
      parameters?: {
        _id: string
        title: string
        description: string
        image: any
        productSlug?: string
        variantSlug?: string
        originalPrice: string
        discountedPrice: string
      }[],
      shippingMethodsHeading?: string
      shippingMethods?: {
        _id: string
        title: string
        type: string
        heading: string
        price: string
        description: string
        colorClass: string
        textColorClass: string
        features: {
          icon: string
          description: string
        }[]
      }[]
    }
    product?: any
    discounts?: any
    lineItem?: LineItem
  }
}

type MODAL_VIEWS =
  'UPSELL_VIEW' | 'CART_CONFLICT_VIEW_SUBSCRIPTION' | 'CART_CONFLICT_VIEW_ONE_TIME' | 'CONFIRM_CANCEL_SUBSCRIPTION' | 'SHARE_KIT'
  // | 'LOGIN_VIEW'
  // | 'FORGOT_VIEW'
  // | 'NEW_SHIPPING_ADDRESS'
  // | 'NEW_PAYMENT_METHOD'

type SIDEBAR_VIEWS = 'CART_VIEW' | 'CHECKOUT_VIEW' | 'PAYMENT_METHOD_VIEW' | 'PROFILE_VIEW'

export const UIContext = React.createContext<State | any>(initialState)

UIContext.displayName = 'UIContext'

function uiReducer(state: State, action: Action) {
  switch (action.type) {
    case 'OPEN_SIDEBAR': {
      return {
        ...state,
        displaySidebar: true
      }
    }
    case 'CLOSE_SIDEBAR': {
      return {
        ...state,
        displaySidebar: false
      }
    }
    case 'OPEN_DROPDOWN': {
      return {
        ...state,
        displayDropdown: true
      }
    }
    case 'CLOSE_DROPDOWN': {
      return {
        ...state,
        displayDropdown: false
      }
    }
    case 'OPEN_MODAL': {
      return {
        ...state,
        onCloseModalCallback: action.onCloseCallback,
        displayModal: true,
        modalHideCloseButton: action.modalHideCloseButton ?? false,
        modalMobileFullscreen: action.modalMobileFullscreen ?? false,
        displaySidebar: false
      }
    }
    case 'CLOSE_MODAL': {
      return {
        ...state,
        displayModal: false
      }
    }
    case 'SET_MODAL_VIEW': {
      return {
        ...state,
        modalView: action.view
      }
    }
    case 'SET_SIDEBAR_VIEW': {
      return {
        ...state,
        sidebarView: action.view
      }
    }
    case 'SET_USER_AVATAR': {
      return {
        ...state,
        userAvatar: action.value
      }
    }
    case 'SET_PROD_INFO': {
      return {
        ...state,
        prodInfo: action.value
      }
    }
    case 'SET_PROD_INFO2': {
      return {
        ...state,
        prodInfo2: action.value
      }
    }
    case 'SET_HOW_TO': {
      return {
        ...state,
        howToVideo: action.value
      }
    }
    case 'SET_SHAREABLE_KIT': {
      return {
        ...state,
        shareableKit: action.value
      }
    }
    case 'SET_SHAREABLE_CLIENT': {
      return {
        ...state,
        shareableClient: action.value
      }
    }
    case 'SET_SUBSCRIPTION_BENEFITS': {
      return {
        ...state,
        subscriptionBenefits: action.value
      }
    }
    case 'SET_SUBSCRIPTION_CANCEL_REASONS': {
      return {
        ...state,
        subscriptionCancelReasons: action.value
      }
    }
    case 'SET_UPSELL_INFO': {
      return {
        ...state,
        upsellInfoOnly: action.value?.infoOnly ?? false,
        upsellParametersStep: action.value?.parametersStep ?? true,
        upsellShippingStep: action.value?.shippingStep ?? true,
        upsellSubscriptionStep: action.value?.subscriptionStep ?? true,
        upsellSubscriptionConfig: action.value?.subscriptionConfig ?? true,
        upsellProductUpsell: action.value?.productUpsell ?? false,
        upsellParentKitId: action.value?.parentKitId,
        upsellingItems: action.value?.upsellingItems,
        upsellProduct: action.value?.product,
        upsellDiscounts: action.value?.discounts,
        upsellLineItem: action.value?.lineItem
      }
    }
  }
}

export const UIProvider: FC<{ children?: React.ReactNode }> = (props) => {
  const [state, dispatch] = React.useReducer(uiReducer, initialState)

  const openSidebar = useCallback(
    () => dispatch({ type: 'OPEN_SIDEBAR' }),
    [dispatch]
  )
  const closeSidebar = useCallback(
    () => dispatch({ type: 'CLOSE_SIDEBAR' }),
    [dispatch]
  )
  const toggleSidebar = useCallback(
    () =>
      state.displaySidebar
        ? dispatch({ type: 'CLOSE_SIDEBAR' })
        : dispatch({ type: 'OPEN_SIDEBAR' }),
    [dispatch, state.displaySidebar]
  )
  const closeSidebarIfPresent = useCallback(
    () => state.displaySidebar && dispatch({ type: 'CLOSE_SIDEBAR' }),
    [dispatch, state.displaySidebar]
  )

  const openDropdown = useCallback(
    () => dispatch({ type: 'OPEN_DROPDOWN' }),
    [dispatch]
  )
  const closeDropdown = useCallback(
    () => dispatch({ type: 'CLOSE_DROPDOWN' }),
    [dispatch]
  )

  const openModal = useCallback(
    (onCloseCallback?: (result?: any) => void, modalHideCloseButton: boolean = false, modalMobileFullscreen: boolean = false) => dispatch({ type: 'OPEN_MODAL', onCloseCallback, modalHideCloseButton, modalMobileFullscreen }),
    [dispatch]
  )
  const closeModal = useCallback(
    (result?: any) => {
      if (state?.onCloseModalCallback != null) {
        state.onCloseModalCallback(result);
      }
      dispatch({ type: 'CLOSE_MODAL' });
    },
    [dispatch, state]
  )

  const setUserAvatar = useCallback(
    (value: string) => dispatch({ type: 'SET_USER_AVATAR', value }),
    [dispatch]
  )

  const setProdInfo = useCallback(
    (value: string) => dispatch({ type: 'SET_PROD_INFO', value }),
    [dispatch]
  )

  const setProdInfo2 = useCallback(
    (value: string) => dispatch({ type: 'SET_PROD_INFO2', value }),
    [dispatch]
  )

  const setHowTo = useCallback(
    (value: string) => dispatch({ type: 'SET_HOW_TO', value }),
    [dispatch]
  )

  const setShareableKit = useCallback(
    (value: string) => dispatch({ type: 'SET_SHAREABLE_KIT', value }),
    [dispatch]
  )

  const setShareableClient = useCallback(
    (value: string) => dispatch({ type: 'SET_SHAREABLE_CLIENT', value }),
    [dispatch]
  )

  const setSubscriptionBenefits = useCallback(
    (value: {
      title: string,
      subtitle: string,
      benefits: {
        title: string
        description: string
        icon: string
      }[],
      faqs: {
        title: string
        description: string
      }[]
    }) => dispatch({ type: 'SET_SUBSCRIPTION_BENEFITS', value }),
    [dispatch]
  )

  const setSubscriptionCancelReasons = useCallback(
    (value: CancelReason[]) => dispatch({ type: 'SET_SUBSCRIPTION_CANCEL_REASONS', value }),
    [dispatch]
  )

  const setUpsellInfo = useCallback(
    (value: {
      infoOnly?: boolean,
      parametersStep?: boolean,
      shippingStep?: boolean,
      subscriptionStep?: boolean,
      productUpsell?: boolean,
      parentKitId?: any,
      subscriptionConfig: any
      upsellingItems?: {
        parametersHeading?: string,
        parametersSubheading?: string,
        parameters?: {
          _id: string
          title: string
          description: string
          image: any
          productSlug?: string
          variantSlug?: string
          originalPrice: string
          discountedPrice: string
        }[],
        shippingMethodsHeading?: string
        shippingMethods?: {
          _id: string
          title: string
          type: string
          heading: string
          price: string
          description: string
          colorClass: string
          textColorClass: string
          features: {
            icon: string
            description: string
          }[]
        }[]
      }
      product?: any,
      discounts?: any,
      lineItem?: any
    }) => dispatch({ type: 'SET_UPSELL_INFO', value }),
    [dispatch]
  )

  const setModalView = useCallback(
    (view: MODAL_VIEWS) => dispatch({ type: 'SET_MODAL_VIEW', view }),
    [dispatch]
  )

  const setSidebarView = useCallback(
    (view: SIDEBAR_VIEWS) => dispatch({ type: 'SET_SIDEBAR_VIEW', view }),
    [dispatch]
  )

  const value = useMemo(
    () => ({
      ...state,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      closeSidebarIfPresent,
      openDropdown,
      closeDropdown,
      openModal,
      closeModal,
      setModalView,
      setSidebarView,
      setUserAvatar,
      setProdInfo,
      setProdInfo2,
      setHowTo,
      setShareableKit,
      setShareableClient,
      setSubscriptionBenefits,
      setSubscriptionCancelReasons,
      setUpsellInfo
    }),
    [state, openSidebar, closeSidebar, toggleSidebar, closeSidebarIfPresent, openDropdown, closeDropdown, openModal, closeModal, setModalView, setSidebarView, setUserAvatar, setProdInfo, setProdInfo2, setHowTo, setShareableKit, setShareableClient, setSubscriptionBenefits, setSubscriptionCancelReasons, setUpsellInfo]
  )

  return <UIContext.Provider value={value} {...props} />
}

export const useUI = () => {
  const context = React.useContext(UIContext)
  if (context === undefined) {
    throw new Error(`useUI must be used within a UIProvider`)
  }
  return context
}

export const ManagedUIContext: FC<{ children?: React.ReactNode }> = ({ children }) => (
  <UIProvider>{children}</UIProvider>
)
